import React, { useMemo } from 'react';
import get from 'lodash/get';
import size from 'lodash/size';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { hasProviderProfileSelector } from '../../../redux/context/selectors';
import NotificationSettings from '../NotificationSettings';
import PrivacySettings from '../PrivacySettings';
import LanguagePicker from '../LanguagePicker';
import DeviceSetup from '../DeviceSetup';
import MfaSettings from '../MfaSettings';
import UnitsSettings from '../UnitsSettings';
import DeleteAccount from '../DeleteAccount';
import { enhance } from './enhance';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';

const ProfileSettings = ({
  canEdit,
  canEditOwnPatient,
  saveSettings,
  profile: {
    id,
    settings,
    phoneNumber,
    email,
    emailFake,
    mfaEnabled,
    phoneNumberConfirmed,
  },
}) => {
  usePageView('Profile | Settings');
  const notifySettings = useNotificationSettings(
    settings,
    email,
    emailFake,
    phoneNumber,
  );
  const isNotProvider = !useSelector(hasProviderProfileSelector);

  const saveLanguageSettings = languageSettings =>
    saveSettings({ id, languageSettings });

  const savePrivacySettings = value => {
    saveSettings({ id, hideFields: value });
  };

  const saveNotificationSettings = (type, enabled) => {
    saveSettings({
      ...settings,
      notificationSettings: {
        [type]: enabled,
      },
      id,
    });
  };

  const safeCredential = useMemo(
    () => (phoneNumberConfirmed ? phoneNumber : email),
    [phoneNumberConfirmed, phoneNumber, email],
  );

  return (
    <div styleName="content content--profile content--info">
      <MetaHelmet title="pages.title.profile.settings" />

      <div styleName="info info--settings">
        <If condition={canEdit && !canEditOwnPatient}>
          <LanguagePicker
            onChange={saveLanguageSettings}
            locale={get(settings, 'languageSettings')}
          />
          <If condition={size(notifySettings)}>
            <NotificationSettings
              settings={notifySettings}
              onChange={saveNotificationSettings}
            />
          </If>
          <If condition={isNotProvider}>
            <MfaSettings
              id={id}
              active={mfaEnabled}
              credential={safeCredential}
            />
          </If>
          <PrivacySettings
            onChange={savePrivacySettings}
            hidden={settings.hideFields}
          />
          <If condition={isNotProvider}>
            <DeviceSetup />
          </If>
          <If condition={isNotProvider}>
            <UnitsSettings />
          </If>
          <DeleteAccount />
        </If>
      </div>
    </div>
  );
};

ProfileSettings.propTypes = {
  canEditOwnPatient: PropTypes.bool,
  canEdit: PropTypes.bool,
  saveSettings: PropTypes.func,
  profile: PropTypes.object,
};

export default enhance(ProfileSettings);

function useNotificationSettings(settings, email, emailFake, phoneNumber) {
  return useMemo(() => {
    const notificationSettings = get(settings, 'notificationSettings', {});
    const { sms: smsEnabled, email: emailEnabled } = notificationSettings;

    const notificationTypes = [];

    if (!emailFake && email) {
      notificationTypes.push({
        type: 'email',
        value: email,
        enabled: emailEnabled,
      });
    }
    if (phoneNumber) {
      notificationTypes.push({
        type: 'sms',
        value: phoneNumber,
        enabled: smsEnabled,
      });
    }

    return notificationTypes;
  }, [settings, email, emailFake, phoneNumber]);
}



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/ProfileSettings/index.jsx